import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBlockConfig,
  getBlockConfigsApi,
  saveBlockConfigApi,
  updateBlockConfigApi,
} from "../../actions/stations";
import Loader from "../../components/loader";
import { useShowToast } from "../../hooks";
import { strings } from "../../I18n";

const UserRestrictionsTab = ({ whiteLabelId }) => {
  const dispatch = useDispatch();
  const showToast = useShowToast();

  const [enabled, setEnabled] = useState(false);
  const [minimumAge, setMinimumAge] = useState(18);
  const [blockMessage, setBlockMessage] = useState("");
  const [existingConfigId, setExistingConfigId] = useState(null);

  const blockConfigs = useSelector((state) => state.stations.blockConfigs);
  const blockConfigsSuccess = useSelector(
    (state) => state.stations.blockConfigsSuccess
  );
  const saveBlockConfigSuccess = useSelector(
    (state) => state.stations.saveBlockConfigSuccess
  );

  useEffect(() => {
    if (whiteLabelId) {
      dispatch(getBlockConfigsApi(whiteLabelId));
    }
  }, [whiteLabelId, dispatch]);

  useEffect(() => {
    if (blockConfigs && blockConfigs.length > 0) {
      const ageConfig = blockConfigs.find(
        (c) => c.blockType === "AGE_VERIFICATION"
      );
      if (ageConfig) {
        setExistingConfigId(ageConfig.id);
        setEnabled(ageConfig.enabled);
        setMinimumAge(ageConfig.config?.minimumAge || 18);
        setBlockMessage(ageConfig.config?.blockMessage || "");
      } else {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [blockConfigs]);

  const resetForm = () => {
    setExistingConfigId(null);
    setEnabled(false);
    setMinimumAge(18);
    setBlockMessage("");
  };

  useEffect(() => {
    if (saveBlockConfigSuccess === true) {
      dispatch(clearBlockConfig());
      showToast("Settings saved successfully", "success");
      dispatch(getBlockConfigsApi(whiteLabelId));
    } else if (saveBlockConfigSuccess === false) {
      dispatch(clearBlockConfig());
      showToast("Failed to save settings", "error");
    }
  }, [saveBlockConfigSuccess]);

  const handleSave = () => {
    const configData = {
      whiteLabelId: whiteLabelId,
      blockType: "AGE_VERIFICATION",
      enabled: enabled,
      config: {
        minimumAge: parseInt(minimumAge, 10),
        blockMessage: blockMessage,
      },
    };

    if (existingConfigId) {
      dispatch(
        updateBlockConfigApi(whiteLabelId, existingConfigId, configData)
      );
    } else {
      dispatch(saveBlockConfigApi(whiteLabelId, configData));
    }
  };

  if (!whiteLabelId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          Please select a whitelabel to configure user restrictions.
        </Typography>
      </Box>
    );
  }

  if (blockConfigsSuccess === null) {
    return <Loader />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {strings("settings.ageVerification") || "Age Verification"}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={
              strings("settings.enableAgeVerification") ||
              "Enable Age Verification"
            }
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth={true}
            type="number"
            label={strings("settings.minimumAge") || "Minimum Age"}
            value={minimumAge}
            onChange={(e) => setMinimumAge(e.target.value)}
            disabled={!enabled}
            inputProps={{ min: 1, max: 100 }}
            helperText={
              strings("settings.minimumAgeHelp") ||
              "Users below this age will be blocked from using the service"
            }
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth={true}
            multiline={true}
            rows={3}
            label={strings("settings.blockMessage") || "Block Message"}
            value={blockMessage}
            onChange={(e) => setBlockMessage(e.target.value)}
            disabled={!enabled}
            helperText={
              strings("settings.blockMessageHelp") ||
              "Message shown to users who are blocked due to age restrictions"
            }
          />
        </Box>

        <Button variant="contained" color="primary" onClick={handleSave}>
          {strings("descriptions.save") || "Save"}
        </Button>
      </Paper>
    </Box>
  );
};

export default UserRestrictionsTab;
